import type { FunctionComponent, SVGProps } from "react"

import AreaLove from "@/assets/img/profile/advisorQuiz/area_love.svg?react"
import AreaCareer from "@/assets/img/profile/advisorQuiz/area_career.svg?react"
import AreaDestiny from "@/assets/img/profile/advisorQuiz/area_destiny.svg?react"
import AreaFamily from "@/assets/img/profile/advisorQuiz/area_family.svg?react"
import AreaFuture from "@/assets/img/profile/advisorQuiz/area_future.svg?react"
import MoodGood from "@/assets/img/profile/advisorQuiz/mood_good.svg?react"
import MoodBetween from "@/assets/img/profile/advisorQuiz/mood_between.svg?react"
import MoodNotGreat from "@/assets/img/profile/advisorQuiz/mood_not_great.svg?react"
import ReadingAstrology from "@/assets/img/profile/advisorQuiz/reading_astrology.svg?react"
import ReadingTarot from "@/assets/img/profile/advisorQuiz/reading_tarot.svg?react"
import ReadingMedium from "@/assets/img/profile/advisorQuiz/reading_medium.svg?react"
import ReadingClairvoyant from "@/assets/img/profile/advisorQuiz/reading_clairvoyant.svg?react"
import ReadingDream from "@/assets/img/profile/advisorQuiz/reading_dream.svg?react"
import InsightKind from "@/assets/img/profile/advisorQuiz/insight_kind.svg?react"
import InsightHonest from "@/assets/img/profile/advisorQuiz/insight_honest.svg?react"
import InsightCalm from "@/assets/img/profile/advisorQuiz/insight_calm.svg?react"
import GoalPeace from "@/assets/img/profile/advisorQuiz/goal_peace.svg?react"
import GoalSteps from "@/assets/img/profile/advisorQuiz/goal_steps.svg?react"
import GoalValidation from "@/assets/img/profile/advisorQuiz/goal_validation.svg?react"
import GoalSign from "@/assets/img/profile/advisorQuiz/goal_sign.svg?react"

type SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>

export type QuizAnswerKey =
  | "focusArea"
  | "mood"
  | "readingType"
  | "communicationStyle"
  | "challenge"
  | "goal"

export interface QuizOption {
  value: string
  label: string
  Icon?: SvgComponent
}

export interface QuizStep {
  key: QuizAnswerKey
  title: string
  subtitle: string
  options: QuizOption[]
}

/** The six question steps of the find-an-advisor quiz. Ported from AdvisorsQuizModal. */
export const QUIZ_STEPS: QuizStep[] = [
  {
    key: "focusArea",
    title: "What kind of guidance do you need most right now?",
    subtitle: "Choose the area you're most focused on",
    options: [
      { value: "loveRelationships", label: "Love & Relationships", Icon: AreaLove },
      { value: "careerWork", label: "Career & Work", Icon: AreaCareer },
      { value: "destinyLifePath", label: "Destiny & Life Path", Icon: AreaDestiny },
      { value: "familyFriends", label: "Family & Friends", Icon: AreaFamily },
      { value: "futureSpirituality", label: "Future & Spirituality", Icon: AreaFuture },
    ],
  },
  {
    key: "mood",
    title: "How are you feeling today?",
    subtitle: "Let us tune into your current energy",
    options: [
      { value: "feelingGood", label: "Feeling good", Icon: MoodGood },
      { value: "inBetween", label: "In between", Icon: MoodBetween },
      { value: "notGreat", label: "Not great", Icon: MoodNotGreat },
    ],
  },
  {
    key: "readingType",
    title: "What type of psychic reading speaks to you?",
    subtitle: "Pick the style that resonates with you most",
    options: [
      { value: "astrology", label: "Astrology", Icon: ReadingAstrology },
      { value: "tarotReading", label: "Tarot Reading", Icon: ReadingTarot },
      { value: "medium", label: "Medium", Icon: ReadingMedium },
      { value: "clairvoyant", label: "Clairvoyant", Icon: ReadingClairvoyant },
      { value: "dreamExploration", label: "Dream Exploration", Icon: ReadingDream },
      { value: "notSure", label: "I’m not sure" },
    ],
  },
  {
    key: "communicationStyle",
    title: "How do you prefer to receive insight?",
    subtitle: "We all resonate with different energies",
    options: [
      { value: "kindUnderstanding", label: "Kind & Understanding", Icon: InsightKind },
      { value: "honestClear", label: "Honest & Clear", Icon: InsightHonest },
      { value: "calmBalanced", label: "Calm & Balanced", Icon: InsightCalm },
    ],
  },
  {
    key: "challenge",
    title: "What’s your biggest challenge right now?",
    subtitle: "Choose the one that hits closest to home",
    options: [
      { value: "feelingStuck", label: "Feeling stuck" },
      { value: "trustIssues", label: "Trust issues" },
      { value: "bigLifeDecisions", label: "Big life decisions" },
      { value: "justConfused", label: "Just confused" },
    ],
  },
  {
    key: "goal",
    title: "What are you hoping to get out of this reading?",
    subtitle: "Let us know what matters most to you",
    options: [
      { value: "peaceOfMind", label: "Peace of mind", Icon: GoalPeace },
      { value: "clearNextSteps", label: "Clear next steps", Icon: GoalSteps },
      { value: "validation", label: "Validation", Icon: GoalValidation },
      { value: "signOrMessage", label: "A sign or message", Icon: GoalSign },
    ],
  },
]

/** Focus area → three matched advisor indices (into advisorsList). */
export const FOCUS_ADVISORS: Record<string, number[]> = {
  loveRelationships: [1, 2, 12],
  careerWork: [6, 9, 10],
  destinyLifePath: [4, 7, 14],
  familyFriends: [0, 11, 13],
  futureSpirituality: [3, 5, 8],
}

export const RESULT_BADGES = ["Deep Listener", "Most accurate", "Fast & Direct"]

export const LOADING_LABELS = [
  "Interpreting your emotional state",
  "Understanding your focus and needs",
  "Aligning with your communication style",
]
